/**
 * The purchase-channel vocabulary (`transactions.platform`, VER-353/VER-358) — the write
 * path's channel markers as they reach the wire. One module so the filter chip, the picker
 * and the detail drawer's "via …" chip can never disagree on labels or grouping.
 *
 * A row's `platform: null` asserts NO channel — never "bought directly" — so there is
 * deliberately no negative filter value and no "sem plataforma" option anywhere.
 */

export const PLATFORM_KEYS = [
  'ifood',
  '99food',
  'mercadolivre',
  'amazon',
  'shopee',
  'google_play',
  'dlocal',
  'paddle',
] as const;

export type PlatformKey = (typeof PLATFORM_KEYS)[number];

/** Single-select filter axis, like PaymentMethodFilter. */
export type PlatformFilter = PlatformKey | null;

export const PLATFORM_LABELS: Record<PlatformKey, string> = {
  ifood: 'iFood',
  '99food': '99Food',
  mercadolivre: 'Mercado Livre',
  amazon: 'Amazon',
  shopee: 'Shopee',
  google_play: 'Google Play',
  dlocal: 'dLocal',
  paddle: 'Paddle',
};

/**
 * Marketplaces are channels the user recognizes as "where I bought"; processors (the
 * merchant-of-record class: dLocal, Paddle) are true facts about the money's path that
 * nobody thinks of as a store — the picker separates them so the honest-but-unfamiliar
 * names don't dilute the recognizable ones (Davi, 2026-08-15).
 */
export const PLATFORM_GROUPS: ReadonlyArray<{
  label: string;
  keys: readonly PlatformKey[];
}> = [
  { label: 'Marketplaces e lojas', keys: ['ifood', '99food', 'mercadolivre', 'amazon', 'shopee', 'google_play'] },
  { label: 'Processadores internacionais', keys: ['dlocal', 'paddle'] },
];

export function isPlatformKey(value: string | null | undefined): value is PlatformKey {
  return !!value && (PLATFORM_KEYS as readonly string[]).includes(value);
}

/** Display label for a wire value; falls back to the raw value so an unknown-but-stamped
 *  channel (vocabulary grows on the write path first) still renders honestly. */
export function platformLabel(value: string): string {
  return isPlatformKey(value) ? PLATFORM_LABELS[value] : value;
}
